import React, {useEffect, useState, useMemo} from 'react';
import { FiArrowUpRight, FiMic, FiMicOff, FiUsers, FiArrowDownLeft, FiPhoneMissed, FiPhoneOff } from 'react-icons/fi';
import { BsClipboardCheck, BsKeyboard } from 'react-icons/bs';
import { HiOutlinePause } from 'react-icons/hi2';
import { LuNotebookPen } from 'react-icons/lu';
import { interactions } from '../ctiData';
import { formatSeconds } from '../ctiHelpers';
import { useCallStore } from "../../../router/zustand/callStore";

function OnCallScreen({ holdOn, muteOn, onToggleHold,
  onToggleMute, onGoConference, campaigns,
  activeCampaign, activeCampaignLoading,
  screen, onReject, agentCallsData}) {
  const [callStartTime, setCallStartTime] = useState(null);
  const [callSeconds, setCallSeconds] = useState(0);
  const callerNumber = useCallStore((state) => state.callerNumber);
  const sipLeadData = useCallStore((state) => state.sipLeadData);

  useEffect(() => {
    let timer;

    if (screen === "oncall") {
      if (!callStartTime) {
        setCallStartTime(Date.now());
      }

      timer = setInterval(() => {
        setCallSeconds(Math.floor((Date.now() - callStartTime) / 1000));
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [screen, callStartTime]);

  useEffect(() => {
    if (screen !== "oncall") {
      setCallStartTime(null);
      setCallSeconds(0);
    }
  }, [screen]);

  const callRecords = useMemo(
    () => agentCallsData?.callRecords || [],
    [agentCallsData]
  );

  const formatCallLogTimestamp = (value) => {
    if (!value) return "--:--";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "--:--";

    const now = new Date();
    const isToday =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();

    const time = date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });

    if (isToday) return `Today - ${time}`;

    const dateParts = new Intl.DateTimeFormat("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).formatToParts(date);

    const getPart = (type) => dateParts.find((p) => p.type === type)?.value || "";
    const day = getPart("day");
    const month = getPart("month");
    const year = getPart("year");

    return `${day} ${month}, ${year} - ${time}`.trim();
  };

  const formattedCallLogs = useMemo(() => {
    if (!callRecords?.length) return [];

    return [...callRecords] // 👈 copy to avoid mutating original
      .sort((a, b) => {
        const timeA = new Date(a.time_of_call || a.created_at).getTime();
        const timeB = new Date(b.time_of_call || b.created_at).getTime();
        return timeB - timeA; // latest first
      })
      .slice(0, 5) // 👈 last 5 interactions
      .map((record) => {
        const isIncoming = record?.direction === "incoming";
        const answerStatus =
          record?.answer_status || (record?.answered ? "answered" : "not_answered");
        const isAnswered =
          answerStatus === "answered" || Boolean(record?.answered);

        const hangupCause = String(record?.hangup_cause || "").toLowerCase();
        const declinedByAgent =
          hangupCause.includes("declined_by_agent") ||
          hangupCause.includes("declined");

        const callTime = record?.time_of_call || record?.created_at;
        const formattedTime = formatCallLogTimestamp(callTime);

        return {
          id: record?.id,
          icon: isAnswered
            ? isIncoming
              ? <FiArrowDownLeft />
              : <FiArrowUpRight />
            : isIncoming
            ? declinedByAgent
              ? <FiPhoneOff />
              : <FiPhoneMissed />
            : <FiPhoneOff />,
          color: isAnswered
            ? isIncoming
              ? "var(--green)"
              : "var(--blue)"
            : "var(--red)",
          name: record?.name || record?.caller_profile || "Unknown",
          num:
            record?.number ||
            record?.caller_number ||
            record?.callee_number ||
            "-",
          dur: isAnswered
            ? formatSeconds(Number(record?.billing_duration) || 0)
            : isIncoming
            ? declinedByAgent
              ? "Declined"
              : "Missed"
            : "Declined",
          time: formattedTime,
          danger: !isAnswered,
        };
      });
  }, [callRecords]);
  
  const currentCampaign = activeCampaign || {};
  const scriptData =
    currentCampaign?.voice_campaigns_scripts_details ||
    currentCampaign?.script ||
    {
      title: null,
      short_description: null,
      description: null,
    };

  const isRecordingEnabled = (() => {
    const value = currentCampaign?.is_recording;
    if (value === true) return true;
    if (value === 1) return true;
    if (typeof value === "string" && value.trim() === "1") return true;
    return false;
  })();

  return (
    <div className="screen active">
      <div className="oncall-layout">
        <div className="panel" style={{marginRight: '5px'}}>
          <div className="call-header">
            <div className="caller-mini-avatar">A1</div>
            <div className="flex-1">
              <div className="caller-name-sm">
                {sipLeadData?.first_name || sipLeadData?.last_name
                  ? `${sipLeadData?.first_name || ""} ${sipLeadData?.last_name || ""}`.trim()
                  : "Unknown"}
              </div>
              <div className="caller-num-sm">{callerNumber || "-"}</div>
            </div>
            <div className={`call-timer ${holdOn ? 'hold' : ''}`}>{formatSeconds(callSeconds)}</div>
          </div>
          <div className="ctrl-grid">
            <button
              type="button"
              className={`ctrl-btn hold ${holdOn ? 'active-hold' : ''}`}
              onClick={onToggleHold}
              style={{ position: "relative" }}
              id="hold"
            >
              <span className="ctrl-icon"><HiOutlinePause /></span>
              <span className="ctrl-label">{holdOn ? 'Unhold' : 'Hold'}</span>
              <input
                id="hold"
                className="hold"
                type="checkbox"
                checked={holdOn}
                readOnly
                aria-hidden="true"
                tabIndex={-1}
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0,
                  cursor: "pointer",
                }}
              />
            </button>
            <button
              type="button"
              className={`ctrl-btn ${muteOn ? 'active-mute' : ''}`}
              onClick={onToggleMute}
              style={{ position: "relative" }}
            >
              <span className="ctrl-icon">{muteOn ? <FiMicOff /> : <FiMic />}</span>
              <span className="ctrl-label">{muteOn ? 'Unmute' : 'Mute'}</span>
              <input
                id="mute"
                className="mute"
                type="checkbox"
                checked={muteOn}
                readOnly
                aria-hidden="true"
                tabIndex={-1}
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0,
                  cursor: "pointer",
                }}
              />
            </button>
            <button type="button" className="ctrl-btn" onClick={onGoConference}>
              <span className="ctrl-icon"><FiUsers /></span>
              <span className="ctrl-label">Conference</span>
            </button>
            {isRecordingEnabled && (
              <button
                type="button"
                className="ctrl-btn"
                title="Recording is enabled for this campaign"
              >
                <span className="ctrl-icon">REC</span>
                <span className="ctrl-label">Recording</span>
              </button>
            )}
            <button type="button" className="ctrl-btn">
              <span className="ctrl-icon"><FiArrowUpRight /></span>
              <span className="ctrl-label">Transfer</span>
            </button>
            <button type="button" className="ctrl-btn">
              <span className="ctrl-icon"><BsClipboardCheck /></span>
              <span className="ctrl-label">Wrap Code</span>
            </button>
            <button type="button" className="ctrl-btn">
              <span className="ctrl-icon"><LuNotebookPen /></span>
              <span className="ctrl-label">Note</span>
            </button>
            <button type="button" className="ctrl-btn">
              <span className="ctrl-icon"><BsKeyboard /></span>
              <span className="ctrl-label">DTMF</span>
            </button>
            <button type="button" className="ctrl-btn danger-btn hangup" onClick={onReject} id="hangup">
              <span className="ctrl-icon"><FiPhoneOff /></span>
              <span className="ctrl-label">End Call</span>
            </button>
          </div>
          <div className="interactions-wrap">
            <div className="subhead">Last 5 Interactions</div>
              <div
                style={{ height: "250px", maxHeight: "250px", overflowY: "scroll" }}
              >
                {formattedCallLogs.length === 0 ? (
                  <div
                    style={{
                      padding: "10px",
                      textAlign: "center",
                      color: "#9ca3af",
                      fontSize: "13px",
                    }}
                  >
                    No call records found
                  </div>
                ) : (
                  formattedCallLogs.map((item) => (
                    <div className="log-item" key={item.id}>
                      <div className="log-icon" style={{ color: item.color }}>
                        {item.icon}
                      </div>
                      <div className="flex-1">
                        <div className="log-name">{item.name}</div>
                        <div className="log-num">{item.num}</div>
                      </div>
                      <div className="text-right">
                        <div
                          className="log-dur"
                          style={item.danger ? { color: "var(--red)" } : undefined}
                        >
                          {item.dur}
                        </div>
                        <div className="log-time">{item.time}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
          </div>
        </div>

        {/* ==================== DYNAMIC CALL SCRIPT PANEL ==================== */}
        <div className="panel" style={{marginRight: '5px'}}>
          <div className="panel-header">
            <span className="panel-title">Call Script</span>
            <span className="script-chip">
              {activeCampaignLoading
                ? "Loading..."
                : scriptData.title || currentCampaign.campaign_name || 'No Script Assigned'}
            </span>
          </div>

          <div className="script-wrap">
            {/* Structured dynamic blocks using ALL available data from the campaign + script */}
            <div className="script-block">
              <span className="script-tag">Campaign Name</span>
              {currentCampaign.campaign_name || 'N/A'}
            </div>

            {scriptData.title && (
              <div className="script-block">
                <span className="script-tag">Script Title</span>
                {scriptData.title}
              </div>
            )}

            {scriptData.short_description && (
              <div className="script-block">
                <span className="script-tag">Short Description</span>
                {scriptData.short_description}
              </div>
            )}

            {scriptData.description && (
              <div className="script-block">
                <span className="script-tag">Full Script Content</span>
                <div
                  dangerouslySetInnerHTML={{
                    __html: scriptData.description || '<p>No detailed script available.</p>',
                  }}
                />
              </div>
            )}

            <div className="script-block">
              <span className="script-tag">Start Date</span>
              {currentCampaign.start_date || 'N/A'}
            </div>

            <div className="script-block">
              <span className="script-tag">End Date</span>
              {currentCampaign.end_date || 'N/A'}
            </div>

            {currentCampaign.locations && currentCampaign.locations.length > 0 && (
              <div className="script-block">
                <span className="script-tag">Location</span>
                {currentCampaign.locations[0].location_name}
              </div>
            )}

            <div className="script-block">
              <span className="script-tag">Total Numbers</span>
              {currentCampaign.total_nos || '0'}
            </div>

            {/* Keep the original compliance note at the bottom */}
            {isRecordingEnabled &&
              <div className="compliance-note">
                <div className="script-tag">Compliance Note</div>
                <div className="tiny-copy">
                  Call Recording is available for current campaign. Please ensure compliance with all applicable laws and regulations when using call recording features.
                </div>
              </div>
            }
          </div>
        </div>

        {/* <div className="stack-col">
          <div className="panel fill-panel">
            <div className="panel-header">
              <span className="panel-title">AI Copilot</span>
              <span className="live-chip">LIVE</span>
            </div>
            <div className="ai-wrap">
              <div className="ai-hint">
                <div className="ai-tag">Conversion Signal</div>
                <div className="ai-text">
                  Customer mentioned <strong>EMI review</strong> in last call. Lead in closing stage - use{' '}
                  <strong>urgency framing</strong> around month-end offer.
                </div>
              </div>
              <div className="ai-hint">
                <div className="ai-tag">Product Match</div>
                <div className="ai-text">
                  Based on Rs. 45L loan and 782 credit score, she qualifies for{' '}
                  <strong>Smart Home Flexi Plan</strong> at 8.25% - not yet offered.
                </div>
              </div>
              <div className="ai-hint amber-ai">
                <div className="ai-tag amber-text">Sentiment Alert</div>
                <div className="ai-text">
                  Previous notes show <strong>price sensitivity</strong>. Avoid mentioning fees early. Lead with
                  monthly savings figure.
                </div>
              </div>
              <div className="ai-hint green-ai">
                <div className="ai-tag green-text">Recommended Next Step</div>
                <div className="ai-text">
                  Offer to send <strong>EMI comparison table via WhatsApp</strong> while on call. 73% conversion lift
                  for this segment.
                </div>
              </div>
              <div className="ai-hint">
                <div className="ai-tag">Similar Conversions</div>
                <div className="ai-text">
                  Agents who offered <strong>home documentation pickup</strong> saw 2.4x higher close rate with this
                  customer profile this month.
                </div>
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="panel-header">
              <span className="panel-title">Customer Snapshot</span>
            </div>
            <div className="snapshot-wrap">
              <div className="crm-field">
                <span className="crm-key">Loan Ask</span>
                <span className="crm-val blue">Rs. 45,00,000</span>
              </div>
              <div className="crm-field">
                <span className="crm-key">Credit Score</span>
                <span className="crm-val green">782 up</span>
              </div>
              <div className="crm-field">
                <span className="crm-key">Lead Score</span>
                <span className="crm-val amber">HOT 91/100</span>
              </div>
              <div className="crm-field">
                <span className="crm-key">Preferred Time</span>
                <span className="crm-val">Afternoon</span>
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
}

export default React.memo(OnCallScreen);